/**
 * «Faststart»: el índice de un MP4 (`moov`) al principio del archivo.
 *
 * Muchos vídeos —los del celular, los de varios editores— lo guardan al final,
 * detrás de los datos (`mdat`). Así el navegador no sabe nada del vídeo hasta
 * haberlo bajado casi entero: en una página se ve negro varios segundos antes
 * del primer cuadro. Moverlo delante no recomprime nada: es reordenar las cajas
 * y corregir dónde empieza cada trozo de datos (`stco`/`co64`), que se corre
 * exactamente lo que mide el índice.
 *
 * Devuelve el archivo reordenado, o `null` si ya estaba bien o no se entiende
 * (entonces se sube tal cual: nunca se arriesga a romperlo).
 */
function readBoxes(view, start, end) {
    const boxes = [];
    let at = start;
    while (at + 8 <= end) {
        let size = view.getUint32(at);
        const type = String.fromCharCode(view.getUint8(at + 4), view.getUint8(at + 5), view.getUint8(at + 6), view.getUint8(at + 7));
        let header = 8;
        if (size === 1) {
            if (at + 16 > end)
                return null;
            const high = view.getUint32(at + 8);
            const low = view.getUint32(at + 12);
            size = high * 2 ** 32 + low;
            header = 16;
        }
        else if (size === 0) {
            size = end - at;
        }
        if (size < header || at + size > end)
            return null;
        boxes.push({ type, start: at, size, header });
        at += size;
    }
    return at === end ? boxes : null;
}
/** Las cajas que hay que abrir para llegar a las tablas de trozos. */
const CONTAINERS = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl', 'edts', 'mvex']);
/** Corre `delta` bytes los inicios de trozo de todas las pistas. `false` si no cabe. */
function shiftChunkOffsets(view, box, delta) {
    const children = readBoxes(view, box.start + box.header, box.start + box.size);
    if (!children)
        return false;
    for (const child of children) {
        if (CONTAINERS.has(child.type)) {
            if (!shiftChunkOffsets(view, child, delta))
                return false;
        }
        else if (child.type === 'stco') {
            const count = view.getUint32(child.start + 12);
            for (let i = 0; i < count; i += 1) {
                const at = child.start + 16 + i * 4;
                const next = view.getUint32(at) + delta;
                if (next > 0xffffffff)
                    return false;
                view.setUint32(at, next);
            }
        }
        else if (child.type === 'co64') {
            const count = view.getUint32(child.start + 12);
            for (let i = 0; i < count; i += 1) {
                const at = child.start + 16 + i * 8;
                const next = view.getUint32(at) * 2 ** 32 + view.getUint32(at + 4) + delta;
                view.setUint32(at, Math.floor(next / 2 ** 32));
                view.setUint32(at + 4, next % 2 ** 32);
            }
        }
    }
    return true;
}
export function faststartMp4(input) {
    const source = new DataView(input);
    const boxes = readBoxes(source, 0, input.byteLength);
    if (!boxes)
        return null;
    const moovIndex = boxes.findIndex((box) => box.type === 'moov');
    const mdatIndex = boxes.findIndex((box) => box.type === 'mdat');
    /* Ya está delante (o no es lo que esperamos): nada que hacer. */
    if (moovIndex < 0 || mdatIndex < 0 || moovIndex < mdatIndex)
        return null;
    if (boxes.filter((box) => box.type === 'moov').length !== 1)
        return null;
    const moov = boxes[moovIndex];
    /* El índice, copiado y con los trozos corridos lo que él mide: va delante de
       todo lo que había antes de él a partir del primer `mdat`. */
    const moovCopy = input.slice(moov.start, moov.start + moov.size);
    const moovView = new DataView(moovCopy);
    if (!shiftChunkOffsets(moovView, { ...moov, start: 0 }, moov.size))
        return null;
    const output = new Uint8Array(input.byteLength);
    const bytes = new Uint8Array(input);
    let at = 0;
    /* Lo de antes del primer `mdat` (ftyp, free…), igual. */
    const firstData = boxes[mdatIndex].start;
    output.set(bytes.subarray(0, firstData), at);
    at += firstData;
    output.set(new Uint8Array(moovCopy), at);
    at += moov.size;
    /* Lo demás, en su orden, sin el `moov` (que ya va delante). */
    for (const box of boxes.slice(mdatIndex)) {
        if (box === moov)
            continue;
        output.set(bytes.subarray(box.start, box.start + box.size), at);
        at += box.size;
    }
    return at === input.byteLength ? output.buffer : null;
}
//# sourceMappingURL=mp4Faststart.js.map